'use client';

import Image from 'next/image';
import { useParams, usePathname } from 'next/navigation';
import { useCallback, useState, useEffect, useRef } from 'react';
import {
  ClipboardCheck,
  Cpu,
  Eye,
  Files,
  Home,
  List,
  PanelLeft,
  Puzzle,
  Settings,
  Users,
  Archive,
} from 'lucide-react';
import Tooltip from '~/components/Tooltip';
import TruncatedText from '~/components/TruncatedText';
import PendingLink from '~/components/Navigation/PendingLink';
import { isPathOrDescendant, usePendingHref } from '~/components/Navigation/routeNavigationStore';
import { Routes } from '~/config';
import useAbility from '~/hooks/useAbility';
import { localStorageKeys, useLocalStorage } from '~/hooks/useLocalStorage';
import { useResponsiveSidebar } from '~/hooks/useResponsiveSidebar';
import { api } from '~/utils/api';
import { serverError } from '~/utils/serverError';
import { useViewerBootstrap } from '~/contexts/ViewerBootstrapContext';
import { Feature, Permission, TaskManagerType } from '@prisma/client';
import { cn } from '~/lib/utils';

import logo from '../../../../public/logo.png';
import HelpButton from './HelpButton';
import HookshotIcon from './HookshotIcon';
import MarketingApprovalMenu from './MarketingApprovalMenu';
import MonitoringMenu from './MonitoringMenu';
import PeopleMenu from './PeopleMenu';
import NavBarItem from './NavBarItem';
import Notifications from './Notifications';
import UserIconButton from './UserIconButton';

function BrandMark({ alt = 'Protege', className }: { alt?: string; className?: string }) {
  return (
    <Image
      src={logo.src}
      alt={alt}
      width={14}
      height={16}
      className={cn('h-[15.3px] w-auto', className)}
    />
  );
}

const subLinkClass = (active: boolean) =>
  cn(
    'ml-6 flex w-[calc(100%-1.5rem)] min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm no-underline outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50',
    active
      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
      : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground',
  );

const flyoutLinkClass = (active: boolean) =>
  cn(
    'flex w-full min-w-0 items-center gap-2 rounded-md px-3 py-2 text-sm no-underline outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50',
    active
      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
      : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground',
  );

const countBadge = (count: number) => (
  <span className="text-[11px] font-medium text-muted-foreground tabular-nums">
    {count > 999 ? '999+' : count}
  </span>
);

const nestedPanelClass = 'flex flex-col gap-0.5 overflow-hidden rounded-lg';

/** Scroll list only — popover paper already owns border/shadow/fill. */
const flyoutPanelClass = 'flex max-h-64 w-52 flex-col overflow-y-auto p-1';

const flyoutSectionLabelClass =
  'text-muted-foreground px-3 py-2 text-[11px] font-medium uppercase tracking-[0.08em]';

const truncateLabel = (label: string) => (
  <TruncatedText className="min-w-0 flex-1">{label}</TruncatedText>
);
const MOBILE_SHEET_BREAKPOINT = 768;

export default function NavBar() {
  const pathname = usePathname();
  const params = useParams();

  // Extract workspaceId from pathname and store in localStorage
  const [storedWorkspaceId, setStoredWorkspaceId] = useLocalStorage(
    localStorageKeys.currentWorkspaceId,
    null,
  );

  // Extract workspaceId from pathname (UUID pattern)
  const workspaceIdFromPath = pathname
    ? pathname.match(/^\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)?.[1]
    : null;

  // Update localStorage when workspaceId is detected in path
  useEffect(() => {
    if (workspaceIdFromPath && workspaceIdFromPath !== storedWorkspaceId) {
      setStoredWorkspaceId(workspaceIdFromPath);
    }
  }, [workspaceIdFromPath, storedWorkspaceId, setStoredWorkspaceId]);

  // Use workspaceId from path first, fallback to stored. Bare `/` is safe here:
  // the secured root page redirects through the server-side workspace picker.
  const workspaceId = workspaceIdFromPath || storedWorkspaceId;
  const homeHref = workspaceId ? `/${workspaceId}` : '/';

  const bootstrappedAuthorized = useViewerBootstrap();
  const { data: authorizedQuery } = api.user.authorize.useQuery(undefined, {
    enabled: !bootstrappedAuthorized,
    onError: serverError,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const authorized = bootstrappedAuthorized ?? authorizedQuery;
  const ability = useAbility();

  const enabledFeatures = authorized?.user.tenant.enabledFeatures;
  const pendingHref = usePendingHref();

  const getIsActive = (route: Routes) => {
    const activePath = pendingHref || pathname;
    // Handle workspace-scoped routes
    if (workspaceId && activePath?.includes(workspaceId)) {
      // Extract the route part after workspace ID
      const pathParts = activePath.split('/');
      const workspaceIndex = pathParts.indexOf(workspaceId);
      const routeAfterWorkspace = pathParts[workspaceIndex + 1];
      return routeAfterWorkspace === route.split('/')[1];
    }
    // Regular route matching
    return activePath?.split('/')[1] === route.split('/')[1];
  };

  const isHrefActive = (href: string, pathActive: boolean) => {
    if (!pendingHref) return pathActive;
    return isPathOrDescendant(pendingHref, href);
  };

  // Helper to get the correct URL (with or without workspace prefix)
  const getUrl = (route: string): string => {
    if (workspaceId) {
      const cleanPath = route.startsWith('/') ? route.slice(1) : route;
      return `/${workspaceId}/${cleanPath}`;
    }
    return route;
  };

  const [isSidebarExpanded, setIsSidebarExpanded] = useLocalStorage(
    localStorageKeys.sidebarExpanded,
    true,
  );
  const [isMarketingTasksExpanded, setIsMarketingTasksExpanded] = useState(false);
  const [isMonitoringExpanded, setIsMonitoringExpanded] = useState(false);
  const [isPeopleExpanded, setIsPeopleExpanded] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_SHEET_BREAKPOINT - 1}px)`);
    const sync = () => setIsMobileViewport(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener('change', sync);
    return () => mediaQuery.removeEventListener('change', sync);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarExpanded((prevIsExpanded) => !prevIsExpanded);
    setIsMarketingTasksExpanded(false);
    setIsMonitoringExpanded(false);
    setIsPeopleExpanded(false);
  }, [setIsSidebarExpanded]);

  // Phone: expanded rail becomes a left sheet overlay — close it after navigation.
  const previousPathnameRef = useRef(pathname);
  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (isMobileViewport && isSidebarExpanded) {
      setIsSidebarExpanded(false);
    }
  }, [pathname, isMobileViewport, isSidebarExpanded, setIsSidebarExpanded]);

  // Auto-expand/close folders based on current page
  useEffect(() => {
    // Marketing folder: expand if on Projects page, close if not
    if (getIsActive(Routes.PROJECTS)) {
      if (!isMarketingTasksExpanded) {
        setIsMarketingTasksExpanded(true);
      }
    } else {
      if (isMarketingTasksExpanded) {
        setIsMarketingTasksExpanded(false);
      }
    }

    // Monitoring folder: expand if on any monitoring page, close if not
    const isOnMonitoringPage =
      getIsActive(Routes.CONTENT_SCANNER) ||
      getIsActive(Routes.INFLUENCER_MONITORING) ||
      getIsActive(Routes.REMEDIATION);

    if (isOnMonitoringPage) {
      if (!isMonitoringExpanded) {
        setIsMonitoringExpanded(true);
      }
    } else {
      if (isMonitoringExpanded) {
        setIsMonitoringExpanded(false);
      }
    }

    // People folder: expand if on any people page, close if not
    const isOnPeoplePage =
      getIsActive(Routes.SELF_REPORTING) ||
      getIsActive(Routes.EMPLOYEE_REVIEW) ||
      getIsActive(Routes.COMMUNICATION_REVIEW);

    if (isOnPeoplePage) {
      if (!isPeopleExpanded) {
        setIsPeopleExpanded(true);
      }
    } else {
      if (isPeopleExpanded) {
        setIsPeopleExpanded(false);
      }
    }
  }, [pathname]);

  // Auto-collapse sidebar on smaller screens
  useResponsiveSidebar({
    isSidebarExpanded,
    toggleSidebar,
    breakpoint: 1024, // Collapse at lg breakpoint
  });

  const { data: projects } = api.task.getProjects.useQuery(
    {
      type: TaskManagerType.MARKETING,
      workspaceId: workspaceId!,
    },
    {
      enabled: !!workspaceId,
      onError: serverError,
    },
  );

  const { data: marketingTaskCount } = api.task.getActiveTaskCount.useQuery(
    {
      type: TaskManagerType.MARKETING,
    },
    {
      onError: serverError,
    },
  );

  const { data: remediationTaskCount } = api.task.getActiveTaskCount.useQuery(
    {
      type: TaskManagerType.REMEDIATION,
    },
    {
      onError: serverError,
    },
  );

  const showMobileSheet = isMobileViewport && isSidebarExpanded;
  const railExpanded = isMobileViewport ? showMobileSheet : isSidebarExpanded;

  const peopleDefaultHref = enabledFeatures?.includes(Feature.EMPLOYEE_REVIEW)
    ? getUrl(Routes.SELF_REPORTING)
    : getUrl(Routes.COMMUNICATION_REVIEW);

  return (
    <>
      {showMobileSheet ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-label="Close navigation menu"
          onClick={toggleSidebar}
        />
      ) : null}
      {/* Keep collapsed rail width in document flow while the sheet overlays */}
      {showMobileSheet ? <div className="w-12 shrink-0" aria-hidden /> : null}
      <nav
        aria-label="Main"
        className={cn(
          'bg-sidebar text-sidebar-foreground flex h-full shrink-0 flex-col overflow-x-hidden overflow-y-auto px-2 py-3 transition-all duration-200 motion-reduce:transition-none',
          showMobileSheet
            ? 'fixed inset-y-0 left-0 z-50 w-52 shadow-lg'
            : !railExpanded
              ? 'w-12'
              : 'w-52',
        )}
      >
        <div className="relative h-7 w-full shrink-0">
          {/* Pinned at left-0.5 in both rail widths: 28px mark shares the collapsed icon axis and does not shift on expand. */}
          <div className="absolute top-1/2 left-0.5 -translate-y-1/2">
            {railExpanded ? (
              <span className="inline-flex size-7 items-center justify-center" aria-hidden>
                <span className="bg-sidebar-primary inline-flex size-[23.8px] items-center justify-center rounded-sm">
                  <BrandMark alt="" />
                </span>
              </span>
            ) : (
              <Tooltip
                content="Expand sidebar"
                hideIcon
                delay={300}
                isNotInteractive
                tooltipClassName="whitespace-nowrap"
                popoverProps={{
                  disablePortal: false,
                  anchorOrigin: {
                    vertical: 'center',
                    horizontal: 'right',
                  },
                  transformOrigin: {
                    vertical: 'center',
                    horizontal: 'left',
                  },
                  slotProps: {
                    paper: {
                      sx: { marginLeft: '8px' },
                    },
                  },
                }}
              >
                <button
                  type="button"
                  className="group inline-flex size-7 items-center justify-center rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  onClick={toggleSidebar}
                  aria-label="Open navigation menu"
                  aria-expanded={false}
                >
                  <span className="bg-sidebar-primary group-hover:bg-sidebar-primary/90 inline-flex size-[23.8px] items-center justify-center rounded-sm transition-colors">
                    <BrandMark alt="" />
                  </span>
                </button>
              </Tooltip>
            )}
          </div>

          <div
            className={cn(
              'absolute top-1/2 right-1 -translate-y-1/2 transition-opacity duration-200 motion-reduce:transition-none',
              railExpanded ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <Tooltip
              content="Collapse sidebar"
              hideIcon
              delay={300}
              isNotInteractive
              tooltipClassName="whitespace-nowrap"
              popoverProps={{
                disablePortal: false,
                anchorOrigin: {
                  vertical: 'center',
                  horizontal: 'right',
                },
                transformOrigin: {
                  vertical: 'center',
                  horizontal: 'left',
                },
                slotProps: {
                  paper: {
                    sx: { marginLeft: '8px' },
                  },
                },
              }}
            >
              <button
                type="button"
                className="text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground inline-flex size-7 items-center justify-center rounded-md outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50"
                onClick={toggleSidebar}
                aria-label="Collapse sidebar"
                aria-expanded={railExpanded}
                aria-hidden={!railExpanded}
                tabIndex={railExpanded ? 0 : -1}
              >
                <PanelLeft className="size-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="relative mb-4 mt-3 flex w-full grow flex-col justify-start gap-0.5">
          <NavBarItem
            label="Home"
            href={homeHref}
            icon={Home}
            isActive={pathname === homeHref}
            isSidebarExpanded={railExpanded}
          />

          {/* Divider 1: Only show if Section 2 (Documents/Marketing) has content */}
          {(enabledFeatures?.includes(Feature.DOCUMENTS) ||
            (enabledFeatures?.includes(Feature.MARKETING_TASKS) &&
              ability?.can('do', Permission.VIEW_MARKETING_TASK))) && (
            <div className="border-sidebar-border my-2 border-t" />
          )}

          {/* Section 2: Content & Workflows */}
          {enabledFeatures?.includes(Feature.DOCUMENTS) && (
            <NavBarItem
              label="Documents"
              href={getUrl(Routes.DOCUMENTS)}
              icon={Files}
              isActive={getIsActive(Routes.DOCUMENTS)}
              isSidebarExpanded={railExpanded}
            />
          )}
          {enabledFeatures?.includes(Feature.MARKETING_TASKS) &&
            ability?.can('do', Permission.VIEW_MARKETING_TASK) && (
              <div className={railExpanded ? 'w-full' : ''}>
                {railExpanded && isMarketingTasksExpanded ? (
                  <div className={nestedPanelClass}>
                    <NavBarItem
                      label="Review Queues"
                      href={getUrl(Routes.PROJECTS)}
                      icon={List}
                      isActive={getIsActive(Routes.PROJECTS)}
                      isSidebarExpanded={railExpanded}
                      badge={
                        marketingTaskCount != null && marketingTaskCount > 0
                          ? countBadge(marketingTaskCount)
                          : undefined
                      }
                      badgeCount={marketingTaskCount ?? 0}
                      onClick={(e) => {
                        if (!!projects?.length) {
                          e.preventDefault();
                          setIsMarketingTasksExpanded((prev) => !prev);
                        }
                      }}
                    />
                    {projects?.map((project) => {
                      const projectHref = getUrl(`${Routes.PROJECTS}/${project.id}/list`);
                      const projectLabel = project.name || project.id;
                      return (
                        <PendingLink
                          key={`Sidebar-Project-${project.id}`}
                          href={projectHref}
                          className={subLinkClass(
                            isHrefActive(projectHref, params?.remoteTaskManagerId === project.id),
                          )}
                        >
                          {truncateLabel(projectLabel)}
                        </PendingLink>
                      );
                    })}
                  </div>
                ) : railExpanded ? (
                  <NavBarItem
                    label="Review Queues"
                    href={getUrl(Routes.PROJECTS)}
                    icon={List}
                    isActive={getIsActive(Routes.PROJECTS)}
                    isSidebarExpanded={railExpanded}
                    asButton={true}
                    badge={
                      marketingTaskCount != null && marketingTaskCount > 0
                        ? countBadge(marketingTaskCount)
                        : undefined
                    }
                    badgeCount={marketingTaskCount ?? 0}
                    onClick={() => {
                      setIsMarketingTasksExpanded(true);
                    }}
                  />
                ) : (
                  <MarketingApprovalMenu
                    content={
                      <div className={flyoutPanelClass}>
                        <div className={flyoutSectionLabelClass}>Review Queues</div>
                        {projects?.map((project) => {
                          const projectHref = getUrl(`${Routes.PROJECTS}/${project.id}/list`);
                          const projectLabel = project.name || project.id;
                          return (
                            <PendingLink
                              key={`Sidebar-Project-${project.id}`}
                              href={projectHref}
                              className={flyoutLinkClass(
                                isHrefActive(
                                  projectHref,
                                  params?.remoteTaskManagerId === project.id,
                                ),
                              )}
                            >
                              {truncateLabel(projectLabel)}
                            </PendingLink>
                          );
                        })}
                      </div>
                    }
                    disabled={false}
                  >
                    <NavBarItem
                      label="Review Queues"
                      icon={List}
                      isActive={getIsActive(Routes.PROJECTS)}
                      isSidebarExpanded={railExpanded}
                      asButton={true}
                      badge={
                        marketingTaskCount != null && marketingTaskCount > 0
                          ? countBadge(marketingTaskCount)
                          : undefined
                      }
                      badgeCount={marketingTaskCount ?? 0}
                    />
                  </MarketingApprovalMenu>
                )}
              </div>
            )}

          {/* Divider 2: Only show if Section 3 (Decision Store/Claims/Policy Engine) has content */}
          {(enabledFeatures?.includes(Feature.DECISION_STORE) ||
            enabledFeatures?.includes(Feature.CLAIMS_APPROVAL) ||
            (enabledFeatures?.includes(Feature.POLICY_ENGINE) &&
              ability?.can('do', Permission.VIEW_POLICY_ENGINE))) && (
            <div className="border-sidebar-border my-2 border-t" />
          )}

          {/* Section 3: Review & Compliance */}
          {enabledFeatures?.includes(Feature.DECISION_STORE) && (
            <NavBarItem
              label="Decision Store"
              href={getUrl(Routes.DECISION_STORE)}
              icon={Puzzle}
              isActive={getIsActive(Routes.DECISION_STORE)}
              isSidebarExpanded={railExpanded}
            />
          )}
          {enabledFeatures?.includes(Feature.CLAIMS_APPROVAL) && (
            <NavBarItem
              label="Claims Approval"
              href={getUrl(Routes.CLAIMS_APPROVAL)}
              icon={ClipboardCheck}
              isActive={getIsActive(Routes.CLAIMS_APPROVAL)}
              isSidebarExpanded={railExpanded}
            />
          )}
          {enabledFeatures?.includes(Feature.POLICY_ENGINE) &&
            ability?.can('do', Permission.VIEW_POLICY_ENGINE) && (
              <NavBarItem
                label="Policy Engine"
                href={getUrl(Routes.POLICY_ENGINE)}
                icon={Cpu}
                isActive={getIsActive(Routes.POLICY_ENGINE)}
                isSidebarExpanded={railExpanded}
              />
            )}

          {/* Divider 3: Only show if Section 4 (Monitoring/People/Archive) has content */}
          {((enabledFeatures?.includes(Feature.CONTENT_SCANNER) &&
            ability?.can('do', Permission.CONTENT_SCANNER)) ||
            (enabledFeatures?.includes(Feature.INFLUENCER_MONITORING) &&
              ability?.can('do', Permission.INFLUENCER_MONITORING)) ||
            (enabledFeatures?.includes(Feature.REMEDIATION) &&
              ability?.can('do', Permission.REMEDIATION_QUEUE)) ||
            enabledFeatures?.includes(Feature.EMPLOYEE_REVIEW) ||
            enabledFeatures?.includes(Feature.COMMUNICATION_REVIEW) ||
            (enabledFeatures?.includes(Feature.ARCHIVE_QUEUE) &&
              ability?.can('do', Permission.ARCHIVE_QUEUE))) && (
            <div className="border-sidebar-border my-2 border-t" />
          )}

          {/* Section 4: Everything Else */}
          {((enabledFeatures?.includes(Feature.CONTENT_SCANNER) &&
            ability?.can('do', Permission.CONTENT_SCANNER)) ||
            (enabledFeatures?.includes(Feature.INFLUENCER_MONITORING) &&
              ability?.can('do', Permission.INFLUENCER_MONITORING)) ||
            (enabledFeatures?.includes(Feature.REMEDIATION) &&
              ability?.can('do', Permission.REMEDIATION_QUEUE))) && (
            <div className={railExpanded ? 'w-full' : ''}>
              {railExpanded && isMonitoringExpanded ? (
                <div className={nestedPanelClass}>
                  <NavBarItem
                    label="Monitoring"
                    href={
                      enabledFeatures?.includes(Feature.CONTENT_SCANNER) &&
                      ability?.can('do', Permission.CONTENT_SCANNER)
                        ? getUrl(Routes.CONTENT_SCANNER)
                        : enabledFeatures?.includes(Feature.INFLUENCER_MONITORING) &&
                            ability?.can('do', Permission.INFLUENCER_MONITORING)
                          ? getUrl(Routes.INFLUENCER_MONITORING)
                          : getUrl(Routes.REMEDIATION)
                    }
                    icon={Eye}
                    isActive={
                      getIsActive(Routes.CONTENT_SCANNER) ||
                      getIsActive(Routes.INFLUENCER_MONITORING) ||
                      getIsActive(Routes.REMEDIATION)
                    }
                    isSidebarExpanded={railExpanded}
                    badge={
                      remediationTaskCount != null && remediationTaskCount > 0
                        ? countBadge(remediationTaskCount)
                        : undefined
                    }
                    badgeCount={remediationTaskCount ?? 0}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMonitoringExpanded((prev) => !prev);
                    }}
                  />
                  {enabledFeatures?.includes(Feature.CONTENT_SCANNER) &&
                    ability?.can('do', Permission.CONTENT_SCANNER) && (
                      <PendingLink
                        href={getUrl(Routes.CONTENT_SCANNER)}
                        className={subLinkClass(getIsActive(Routes.CONTENT_SCANNER))}
                      >
                        Content Scanner
                      </PendingLink>
                    )}
                  {enabledFeatures?.includes(Feature.INFLUENCER_MONITORING) &&
                    ability?.can('do', Permission.INFLUENCER_MONITORING) && (
                      <PendingLink
                        href={getUrl(Routes.INFLUENCER_MONITORING)}
                        className={subLinkClass(getIsActive(Routes.INFLUENCER_MONITORING))}
                      >
                        Influencer
                      </PendingLink>
                    )}
                  {enabledFeatures?.includes(Feature.REMEDIATION) &&
                    ability?.can('do', Permission.REMEDIATION_QUEUE) && (
                      <PendingLink
                        href={getUrl(Routes.REMEDIATION)}
                        className={subLinkClass(getIsActive(Routes.REMEDIATION))}
                      >
                        Remediation
                      </PendingLink>
                    )}
                </div>
              ) : railExpanded ? (
                <NavBarItem
                  label="Monitoring"
                  href={
                    enabledFeatures?.includes(Feature.CONTENT_SCANNER) &&
                    ability?.can('do', Permission.CONTENT_SCANNER)
                      ? getUrl(Routes.CONTENT_SCANNER)
                      : enabledFeatures?.includes(Feature.INFLUENCER_MONITORING) &&
                          ability?.can('do', Permission.INFLUENCER_MONITORING)
                        ? getUrl(Routes.INFLUENCER_MONITORING)
                        : getUrl(Routes.REMEDIATION)
                  }
                  icon={Eye}
                  isActive={
                    getIsActive(Routes.CONTENT_SCANNER) ||
                    getIsActive(Routes.INFLUENCER_MONITORING) ||
                    getIsActive(Routes.REMEDIATION)
                  }
                  isSidebarExpanded={railExpanded}
                  asButton={true}
                  badge={
                    remediationTaskCount != null && remediationTaskCount > 0
                      ? countBadge(remediationTaskCount)
                      : undefined
                  }
                  badgeCount={remediationTaskCount ?? 0}
                  onClick={() => {
                    setIsMonitoringExpanded(true);
                  }}
                />
              ) : (
                <MonitoringMenu
                  content={
                    <div className={flyoutPanelClass}>
                      <div className={flyoutSectionLabelClass}>Monitoring</div>
                      {enabledFeatures?.includes(Feature.CONTENT_SCANNER) &&
                        ability?.can('do', Permission.CONTENT_SCANNER) && (
                          <PendingLink
                            href={getUrl(Routes.CONTENT_SCANNER)}
                            className={flyoutLinkClass(getIsActive(Routes.CONTENT_SCANNER))}
                          >
                            Content Scanner
                          </PendingLink>
                        )}
                      {enabledFeatures?.includes(Feature.INFLUENCER_MONITORING) &&
                        ability?.can('do', Permission.INFLUENCER_MONITORING) && (
                          <PendingLink
                            href={getUrl(Routes.INFLUENCER_MONITORING)}
                            className={flyoutLinkClass(getIsActive(Routes.INFLUENCER_MONITORING))}
                          >
                            Influencer
                          </PendingLink>
                        )}
                      {enabledFeatures?.includes(Feature.REMEDIATION) &&
                        ability?.can('do', Permission.REMEDIATION_QUEUE) && (
                          <PendingLink
                            href={getUrl(Routes.REMEDIATION)}
                            className={flyoutLinkClass(getIsActive(Routes.REMEDIATION))}
                          >
                            Remediation
                          </PendingLink>
                        )}
                    </div>
                  }
                  disabled={false}
                >
                  <NavBarItem
                    label="Monitoring"
                    icon={Eye}
                    isActive={
                      getIsActive(Routes.CONTENT_SCANNER) ||
                      getIsActive(Routes.INFLUENCER_MONITORING) ||
                      getIsActive(Routes.REMEDIATION)
                    }
                    isSidebarExpanded={railExpanded}
                    asButton={true}
                    badge={
                      remediationTaskCount != null && remediationTaskCount > 0
                        ? countBadge(remediationTaskCount)
                        : undefined
                    }
                    badgeCount={remediationTaskCount ?? 0}
                  />
                </MonitoringMenu>
              )}
            </div>
          )}
          {(enabledFeatures?.includes(Feature.EMPLOYEE_REVIEW) ||
            enabledFeatures?.includes(Feature.COMMUNICATION_REVIEW)) && (
            <div className={railExpanded ? 'w-full' : ''}>
              {railExpanded && isPeopleExpanded ? (
                <div className={nestedPanelClass}>
                  <NavBarItem
                    label="People"
                    href={peopleDefaultHref}
                    icon={Users}
                    isActive={
                      getIsActive(Routes.SELF_REPORTING) ||
                      getIsActive(Routes.EMPLOYEE_REVIEW) ||
                      getIsActive(Routes.COMMUNICATION_REVIEW)
                    }
                    isSidebarExpanded={railExpanded}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPeopleExpanded((prev) => !prev);
                    }}
                  />
                  {enabledFeatures?.includes(Feature.EMPLOYEE_REVIEW) && (
                    <PendingLink
                      href={getUrl(Routes.SELF_REPORTING)}
                      className={subLinkClass(getIsActive(Routes.SELF_REPORTING))}
                    >
                      Self-Reporting
                    </PendingLink>
                  )}
                  {enabledFeatures?.includes(Feature.EMPLOYEE_REVIEW) &&
                    ability?.can('do', Permission.VIEW_EMPLOYEE_REVIEW) && (
                      <PendingLink
                        href={getUrl(Routes.EMPLOYEE_REVIEW)}
                        className={subLinkClass(getIsActive(Routes.EMPLOYEE_REVIEW))}
                      >
                        Employee Review
                      </PendingLink>
                    )}
                  {enabledFeatures?.includes(Feature.COMMUNICATION_REVIEW) && (
                    <PendingLink
                      href={getUrl(Routes.COMMUNICATION_REVIEW)}
                      className={subLinkClass(getIsActive(Routes.COMMUNICATION_REVIEW))}
                    >
                      Communication Review
                    </PendingLink>
                  )}
                </div>
              ) : railExpanded ? (
                <NavBarItem
                  label="People"
                  href={peopleDefaultHref}
                  icon={Users}
                  isActive={
                    getIsActive(Routes.SELF_REPORTING) ||
                    getIsActive(Routes.EMPLOYEE_REVIEW) ||
                    getIsActive(Routes.COMMUNICATION_REVIEW)
                  }
                  isSidebarExpanded={railExpanded}
                  asButton={true}
                  onClick={() => {
                    setIsPeopleExpanded(true);
                  }}
                />
              ) : (
                <PeopleMenu
                  content={
                    <div className={flyoutPanelClass}>
                      <div className={flyoutSectionLabelClass}>People</div>
                      {enabledFeatures?.includes(Feature.EMPLOYEE_REVIEW) && (
                        <PendingLink
                          href={getUrl(Routes.SELF_REPORTING)}
                          className={flyoutLinkClass(getIsActive(Routes.SELF_REPORTING))}
                        >
                          Self-Reporting
                        </PendingLink>
                      )}
                      {enabledFeatures?.includes(Feature.EMPLOYEE_REVIEW) &&
                        ability?.can('do', Permission.VIEW_EMPLOYEE_REVIEW) && (
                          <PendingLink
                            href={getUrl(Routes.EMPLOYEE_REVIEW)}
                            className={flyoutLinkClass(getIsActive(Routes.EMPLOYEE_REVIEW))}
                          >
                            Employee Review
                          </PendingLink>
                        )}
                      {enabledFeatures?.includes(Feature.COMMUNICATION_REVIEW) && (
                        <PendingLink
                          href={getUrl(Routes.COMMUNICATION_REVIEW)}
                          className={flyoutLinkClass(getIsActive(Routes.COMMUNICATION_REVIEW))}
                        >
                          Communication Review
                        </PendingLink>
                      )}
                    </div>
                  }
                  disabled={false}
                >
                  <NavBarItem
                    label="People"
                    icon={Users}
                    isActive={
                      getIsActive(Routes.SELF_REPORTING) ||
                      getIsActive(Routes.EMPLOYEE_REVIEW) ||
                      getIsActive(Routes.COMMUNICATION_REVIEW)
                    }
                    isSidebarExpanded={railExpanded}
                    asButton={true}
                  />
                </PeopleMenu>
              )}
            </div>
          )}
          {enabledFeatures?.includes(Feature.ARCHIVE_QUEUE) &&
            ability?.can('do', Permission.ARCHIVE_QUEUE) && (
              <NavBarItem
                label="Archive Queue"
                href={getUrl(Routes.ARCHIVE_QUEUE)}
                icon={Archive}
                isActive={getIsActive(Routes.ARCHIVE_QUEUE)}
                isSidebarExpanded={railExpanded}
              />
            )}
        </div>
        <div className="flex w-full flex-col gap-0.5">
          <NavBarItem
            label="Hookshot"
            href="https://hookshot.tryprotege.com"
            newTab
            icon={HookshotIcon}
            isSidebarExpanded={railExpanded}
          />
          <div className="border-sidebar-border my-2 border-t" />
          <HelpButton isSidebarExpanded={railExpanded} />
          <Notifications isSidebarExpanded={railExpanded} workspaceId={workspaceId ?? undefined} />
          <NavBarItem
            label="Settings"
            href={getUrl(Routes.SETTINGS)}
            icon={Settings}
            isActive={getIsActive(Routes.SETTINGS)}
            isSidebarExpanded={railExpanded}
          />
          <UserIconButton isSidebarExpanded={railExpanded} />
        </div>
      </nav>
    </>
  );
}
